import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { intl } from "LocaleWrapper";
import { useStoreState, Session } from "ezStore";
import { useQuery } from "@apollo/client";

import { QuickFilterTypes, Table } from "@cofano/cck";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { Fields, hasOptionalFieldPermission, hasPluginPermission, usePermission } from "components/utils/Permission";
import { useFrontendTable } from "components/table";

import { relationTypesAndBooleans } from "utils/relationUtils";

import DeleteRelationDialog from "../dialogs/DeleteRelationDialog";

import { ChildPropsFromArray } from "types/commons";

import { GetRelationsDocument, GetRelationsQuery } from "__generated__/view/relations/graphql/relation.queries.generated";
import { RelationType } from "__generated__/enums";

export type RelationFromQuery = ChildPropsFromArray<GetRelationsQuery, "relations">;

const RelationTable = ({ type }: { type: RelationType }) => {

	const navigate = useNavigate();
	const session = useStoreState(store => store.session);

	const [ dialog, setDialog ] = useState<{
		open: boolean,
		relationId: number | null,
	}>({
		open: false,
		relationId: null,
	});

	const { tableId, key, extraColumns, canEdit } = getRelationTypeFields(type, session);

	const { refetch, data, loading } = useQuery<GetRelationsQuery>(GetRelationsDocument, {
		variables: {
			types: [ key ],
		},
	});

	const { tableProps, refetchAfterDelete } = useFrontendTable({
		id: tableId,
		content: data?.relations || [],
		saveForOrganizationEnabled: false,
	});

	return (
		<>
			<Table
				{...tableProps}
				contentLoading={loading}
				onRowDoubleClick={(row, event) => {
					const url = `/view/relations/${row.id}/details`;

					if (event.ctrlKey) {
						window.open(url);
					} else {
						navigate(url);
					}
				}}
				columns={[ {
					id: "name",
					title: intl.formatMessage({ id: "USER_FULLNAME" }),
					sortable: true,
					filterComponent: QuickFilterTypes.TEXT,
				}, {
					id: "externalId",
					title: intl.formatMessage({ id: "EXTERNAL_ID" }),
					sortable: true,
					filterComponent: QuickFilterTypes.TEXT,
					disabled: !hasOptionalFieldPermission(session, Fields.RELATION, "externalId"),
				}, {
					id: "language.code",
					title: intl.formatMessage({ id: "OPERATORS_LANGUAGE" }),
					sortable: true,
				}, {
					id: "email",
					title: intl.formatMessage({ id: "OPERATORS_EMAIL" }),
					sortable: true,
				}, {
					id: "telephone",
					title: intl.formatMessage({ id: "OPERATORS_TELEPHONE" }),
					sortable: true,
				}, {
					id: "address.town",
					title: intl.formatMessage({ id: "OPERATORS_ADDRESS" }),
					sortable: true,
				},
				...extraColumns ]}
				rowActions={[ {
					icon: <EditIcon />,
					onClick: (row: RelationFromQuery) => navigate(`/view/relations/${row.id}/edit`),
					hidden: !canEdit,
				}, {
					icon: <DeleteIcon />,
					onClick: (row: RelationFromQuery) => setDialog({ open: true, relationId: row.id }),
					hidden: (row: RelationFromQuery) => !canEdit || !!row.externalId,
				} ]} />

			{dialog.relationId && (
				<DeleteRelationDialog
					open={dialog.open}
					onClose={() => setDialog({ open: false, relationId: null })}
					relationId={dialog.relationId}
					refetch={() => {
						refetchAfterDelete();
						refetch();
					}} />
			)}
		</>
	);
};

export default RelationTable;

const getExtraColumnsByRelationType = (relationType: RelationType) => {
	const columns = [];
	switch (relationType) {
		case RelationType.Consignor:
			break;
		case RelationType.ContainerOperator:
			break;
		case RelationType.LocationRelation:
			break;
		case RelationType.Operator:
			break;
		case RelationType.ShippingCompany:
			columns.push({
				id: "scac",
				title: intl.formatMessage({ id: "CLIENT_SCAC" }),
				sortable: true,
			});
			break;
		case RelationType.ShipManager:
		case RelationType.Skipper:
			break;
		case RelationType.ApplicationRelation:
		case RelationType.Supplier:
		case RelationType.Customer:
			columns.push({
				id: "paymentCondition",
				title: intl.formatMessage({ id: "CLIENT_PAYMENTS_ARRANGEMENTS" }),
				sortable: true,
			});
			break;
	}
	return columns;
};

const getEditRightsByRelationType = (relationType: RelationType, session: Session) => {
	const { isAdmin, isCompanyUser, isMainUserLimited, isCustomer, isSkipperOrShipManager, isReadOnly } = usePermission();
	const externalRelationManagement = hasPluginPermission(session, "externalRelationManagement");
	//readonly users can by definition not mutate anything so we can safely ignore any logic here and return false
	if (isReadOnly) {
		return { canEdit: false, canDelete: false };
	}
	let canEdit = true, canDelete = true;
	switch (relationType) {
		case RelationType.ApplicationRelation:
			canDelete = isAdmin;
			canEdit = isAdmin;
			break;
		case RelationType.Customer:
			canEdit = isAdmin || isCompanyUser || isMainUserLimited;
			canDelete = !externalRelationManagement;
			break;
		case RelationType.Operator:
			canDelete = !externalRelationManagement && (isAdmin || isCompanyUser || isCustomer || isSkipperOrShipManager || isMainUserLimited);
			canEdit = isAdmin || isCompanyUser || isCustomer || isSkipperOrShipManager || isMainUserLimited;
			break;
		case RelationType.ShipManager:
		case RelationType.Skipper:
			canDelete = isAdmin || isCompanyUser || isCustomer || isSkipperOrShipManager || isMainUserLimited;
			canEdit = isAdmin || isCompanyUser || isCustomer || isSkipperOrShipManager || isMainUserLimited;
			break;
		case RelationType.Supplier:
			canDelete = !externalRelationManagement;
			canEdit = isAdmin || isCompanyUser || isMainUserLimited;
			break;
		case RelationType.Agent:
		case RelationType.ContainerOperator:
		case RelationType.LocationRelation:
		case RelationType.ShippingCompany:
			canDelete = !externalRelationManagement;
			break;
		case RelationType.Broker:
		case RelationType.Consignor:
		case RelationType.Surveyor:
			break;
	}
	return { canEdit, canDelete };
};

const getRelationTypeFields = (relationType: RelationType, session: Session) => {
	const { field, type } = relationTypesAndBooleans.find(rt => rt.key === relationType) || { field: "CUSTOMER", type: RelationType.Customer };
	const extraColumns = getExtraColumnsByRelationType(relationType);
	const { canEdit, canDelete } = getEditRightsByRelationType(relationType, session);
	return { key: type, tableId: field, extraColumns, canEdit, canDelete };
};
